// src/components/ProcessingStep.tsx
import React, { useState, useEffect } from 'react';
import { CheckCircle, AlertCircle, ArrowLeft, Mail, AlertTriangle } from 'lucide-react';

interface ProcessingStepProps {
  files: Array<{
    file: File;
    detectedFormat: string | null;
    userSelectedType: 'trial_balance' | 'profit_loss' | 'balance_sheet' | null;
    rowCount: number;
    fullData: string[][] | null;
  }>;
  onSuccess: (processedData: any) => void;
  onBack: () => void;
}

interface ValidationError {
  type: 'format' | 'data' | 'structure' | 'business_logic' | 'sign_convention';
  message: string;
  row?: number;
  column?: string;
  file?: string;
  severity: 'error' | 'warning';
}

export const ProcessingStep: React.FC<ProcessingStepProps> = ({ files, onSuccess, onBack }) => {
  const [status, setStatus] = useState<'processing' | 'error' | 'success' | 'warnings'>('processing');
  const [errors, setErrors] = useState<ValidationError[]>([]);
  const [warnings, setWarnings] = useState<ValidationError[]>([]);
  const [formatInfo, setFormatInfo] = useState<any[]>([]);
  const [showAllErrors, setShowAllErrors] = useState(false);
  
  // Error threshold for showing sample file warning (set to 10 errors)
  const ERROR_THRESHOLD = 10;

  useEffect(() => {
    const validateFiles = async () => {
      try {
        const filesData = files.map(file => {
          // Ensure fullData exists and has content
          if (!file.fullData || file.fullData.length === 0) {
            return {
              fileName: file.file.name,
              detectedFormat: file.detectedFormat || 'Unknown',
              userSelectedType: file.userSelectedType,
              headers: [],
              rows: [],
              rowCount: file.rowCount
            };
          }
          
          return {
            fileName: file.file.name,
            detectedFormat: file.detectedFormat || 'Unknown',
            userSelectedType: file.userSelectedType,
            headers: file.fullData[0] || [],
            rows: file.fullData.slice(1) || [],
            rowCount: file.rowCount
          };
        });

        console.log('[ProcessingStep] Sending files for enhanced validation:', filesData.length, 'files');

        const response = await fetch('/api/validate-files', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ files: filesData })
        });

        const result = await response.json();
        
        if (!response.ok || !result.success) {
          const allErrors = result.errors || [{ type: 'format', message: 'Unknown validation error', severity: 'error' }];
          const errorsList = allErrors.filter((e: ValidationError) => e.severity === 'error');
          const warningsList = allErrors.filter((e: ValidationError) => e.severity === 'warning');
          
          setErrors(errorsList);
          setWarnings(warningsList);
          setFormatInfo(result.formatInfo || []);
          setStatus('error');
        } else {
          const warningsList = result.errors?.filter((e: ValidationError) => e.severity === 'warning') || [];
          setWarnings(warningsList);
          setFormatInfo(result.formatInfo || []);
          
          if (warningsList.length > 0) {
            setStatus('warnings');
            // Auto-advance after showing warnings for 3 seconds
            setTimeout(() => {
              onSuccess({
                ...result.processedData,
                formatInfo: result.formatInfo,
                warnings: warningsList
              });
            }, 3000);
          } else {
            setStatus('success');
            // Auto-advance to success screen
            setTimeout(() => {
              onSuccess({
                ...result.processedData,
                formatInfo: result.formatInfo
              });
            }, 500);
          }
        }
      } catch (error) {
        console.error('[ProcessingStep] Validation request failed:', error);
        setErrors([{ 
          type: 'format', 
          message: 'Unable to connect to validation service. Please try again.',
          severity: 'error'
        }]);
        setStatus('error');
      }
    };

    validateFiles();
  }, [files, onSuccess]);

  if (status === 'processing') {
    return (
      <div className="max-w-4xl mx-auto text-center">
        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-6"></div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Processing Your Data</h2>
        <p className="text-gray-600">Analyzing file formats and validating data integrity...</p>
      </div>
    );
  }

  if (status === 'warnings') {
    return (
      <div className="max-w-4xl mx-auto text-center">
        <AlertTriangle className="w-16 h-16 text-yellow-500 mx-auto mb-4" />
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Processing Complete with Warnings</h2>
        <p className="text-gray-600 mb-6">Your data was processed successfully, but please review these warnings:</p>

       <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 mb-8">
         <h3 className="text-lg font-semibold text-yellow-800 mb-4">Warnings Found:</h3>
         <ul className="space-y-2 text-left">
           {warnings.map((warning, index) => (
             <li key={index} className="flex items-start text-yellow-700">
               <AlertTriangle className="w-5 h-5 text-yellow-500 mr-2 mt-0.5 flex-shrink-0" />
               <div>
                 <span className="font-medium">
                   {warning.file && `${warning.file}: `}
                   {warning.message}
                 </span>
                 {(warning.row || warning.column) && (
                   <div className="text-sm text-yellow-600 mt-1">
                     {warning.row && `Row ${warning.row}`}
                     {warning.row && warning.column && ', '}
                     {warning.column && `Column ${warning.column}`}
                   </div>
                 )}
               </div>
             </li>
           ))}
         </ul>
       </div>

       {/* Show detected format information - COMMENTED OUT */}

       <p className="text-sm text-gray-500">Proceeding automatically in 3 seconds...</p>
     </div>
   );
 }

  if (status === 'error') {
   const displayedErrors = showAllErrors ? errors : errors.slice(0, 20);
   const hasMoreErrors = errors.length > 20;
   const shouldShowSampleFileWarning = errors.length >= ERROR_THRESHOLD;

   return (
     <div className="max-w-4xl mx-auto">
       {/* Back to Upload Button - Top Left */}
       <div className="mb-6">
         <button
           onClick={onBack}
           className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
         >
           <ArrowLeft className="w-4 h-4 mr-2" />
           Back to Upload
         </button>
       </div>

       <div className="text-center mb-8">
         <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
         <h2 className="text-2xl font-bold text-gray-900 mb-2">Validation Errors Found</h2>
         <p className="text-gray-600">Please fix these issues and try uploading again.</p>
       </div>

       {/* Sample File Warning */}
       {shouldShowSampleFileWarning && (
         <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
           <div className="flex items-center justify-center mb-4">
             <AlertTriangle className="w-6 h-6 text-blue-600 mr-2" />
             <h3 className="text-lg font-semibold text-blue-800">Having Trouble?</h3>
           </div>
           <p className="text-blue-700 text-center mb-4">
             We found {errors.length} validation errors in your files. This usually indicates a formatting issue.
           </p>
           <p className="text-blue-700 text-center">
             <strong>Tip:</strong> Go back to the upload screen and download our sample files to see the correct format.
           </p>
         </div>
       )}

       <div className="bg-red-50 border border-red-200 rounded-lg p-6 mb-8">
         <div className="flex items-center justify-between mb-4">
           <h3 className="text-lg font-semibold text-red-800">Issues Found ({errors.length}):</h3>
           {hasMoreErrors && (
             <button
               onClick={() => setShowAllErrors(!showAllErrors)}
               className="text-sm text-red-600 hover:text-red-800 underline"
             >
               {showAllErrors ? 'Show Less' : `Show All ${errors.length} Errors`}
             </button>
           )}
         </div>
         <div className="max-h-96 overflow-y-auto">
           <ul className="space-y-3">
             {displayedErrors.map((error, index) => (
               <li key={index} className="flex items-start text-red-700 p-3 bg-white rounded border border-red-100">
                 <AlertCircle className="w-5 h-5 text-red-500 mr-3 mt-0.5 flex-shrink-0" />
                 <div className="flex-1 min-w-0">
                   <div className="font-medium break-words">
                     {error.file && (
                       <span className="text-red-900 font-semibold">{error.file}: </span>
                     )}
                     <span className="break-words">{error.message}</span>
                   </div>
                   {(error.row || error.column) && (
                     <div className="text-sm text-red-600 mt-1">
                       {error.row && `Row ${error.row}`}
                       {error.row && error.column && ', '}
                       {error.column && `Column ${error.column}`}
                     </div>
                   )}
                   <div className="text-xs text-red-500 mt-1 capitalize">
                     {error.type.replace('_', ' ')} Error
                   </div>
                 </div>
               </li>
             ))}
           </ul>
           {hasMoreErrors && !showAllErrors && (
             <div className="text-center mt-4 pt-4 border-t border-red-200">
               <p className="text-sm text-red-600">
                 Showing first 20 of {errors.length} errors.
               </p>
               <button
                 onClick={() => setShowAllErrors(true)}
                 className="text-sm text-red-600 hover:text-red-800 underline mt-1"
               >
                 View All Errors
               </button>
             </div>
           )}
         </div>
       </div>

       {/* Show warnings if any exist alongside errors */}
       {warnings.length > 0 && (
         <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 mb-8">
           <h3 className="text-lg font-semibold text-yellow-800 mb-4">Additional Warnings:</h3>
           <ul className="space-y-2">
             {warnings.map((warning, index) => (
               <li key={index} className="flex items-start text-yellow-700">
                 <AlertTriangle className="w-5 h-5 text-yellow-500 mr-2 mt-0.5 flex-shrink-0" />
                 <div>
                   <span className="font-medium">
                     {warning.file && `${warning.file}: `}
                     {warning.message}
                   </span>
                 </div>
               </li>
             ))}
           </ul>
         </div>
       )}

       {/* Show detected format information even for errors - COMMENTED OUT */}

       <div className="flex justify-center">
         <a  
           href="mailto:support@provafi.com?subject=Data Upload Validation Error"
           className="inline-flex items-center px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
         >
           <Mail className="w-5 h-5 mr-2" />
           Contact Support
         </a>
       </div>
     </div>
   );
 }

 return null; // Success state handled by auto-advance
};